/**
 * LandingHero - Full-viewport marketing hero for landing page
 *
 * Purpose: First impression for unauthenticated users
 * Layout: Hero background + left-side content overlay with gradient
 * Animation: Staggered fade-in (wordmark → headline → subhead → CTA → pills)
 */

import { useState, useEffect } from 'react';
import { HeroImage } from './HeroImage';
import { BRAND, BRAND_IMAGES } from '@/styles/tokens';

interface LandingHeroProps {
  onGetStarted?: () => void;
}

const useFadeIn = (delay = 0) => {
  const [mounted, setMounted] = useState(false);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    setTimeout(() => setMounted(true), 100 + delay);
  }, [delay]);

  return {
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(15px)',
    transition: prefersReducedMotion
      ? 'none'
      : 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  };
};

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <circle cx="8" cy="8" r="8" fill={BRAND.tealLight} />
    <path
      d="M5 8.5L7 10.5L11 6.5"
      stroke={BRAND.teal}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function LandingHero({ onGetStarted }: LandingHeroProps) {
  const wordmarkStyle = useFadeIn(300);
  const headlineStyle = useFadeIn(450);
  const subheadStyle = useFadeIn(550);
  const ctaStyle = useFadeIn(650);
  const pillsStyle = useFadeIn(800);

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: BRAND.cream,
        overflow: 'hidden',
      }}
    >
      {/* Hero background */}
      <HeroImage
        src={BRAND_IMAGES.hero}
        height="100vh"
        opacity={0.25}
        objectPosition="60% 50%"
        fadeColor={BRAND.cream}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
        }}
      />

      {/* Content overlay with gradient */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(to right, ${BRAND.cream} 10%, rgba(250,248,243,0.87) 35%, transparent 70%)`,
        }}
      />

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '0 24px',
          width: '100%',
        }}
      >
        <div style={{ maxWidth: '540px' }}>
          {/* TruePrice wordmark */}
          <div
            style={{
              ...wordmarkStyle,
              fontFamily: BRAND.fontSerif,
              fontSize: '22px',
              fontWeight: 'bold',
              marginBottom: '24px',
            }}
          >
            <span style={{ color: BRAND.teal }}>True</span>
            <span style={{ color: BRAND.coral }}>Price</span>
          </div>

          {/* Headline */}
          <h1
            style={{
              ...headlineStyle,
              fontFamily: BRAND.fontSerif,
              fontSize: '36px',
              fontWeight: 'bold',
              letterSpacing: '-0.5px',
              lineHeight: 1.2,
              color: BRAND.ink,
              marginBottom: '16px',
            }}
          >
            Stop guessing.
            <br />
            Start pricing.
          </h1>

          {/* Subhead */}
          <p
            style={{
              ...subheadStyle,
              fontFamily: BRAND.fontSans,
              fontSize: '16px',
              lineHeight: 1.5,
              color: BRAND.inkLight,
              marginBottom: '32px',
              maxWidth: '420px',
            }}
          >
            The forensic pricing tool that shows you what your art is actually worth —
            based on reality, not guesswork.
          </p>

          {/* CTA button */}
          <button
            onClick={onGetStarted}
            style={{
              ...ctaStyle,
              fontFamily: BRAND.fontSans,
              fontSize: '16px',
              fontWeight: 600,
              color: BRAND.white,
              background: `linear-gradient(135deg, ${BRAND.teal}, ${BRAND.tealDark})`,
              padding: '16px 32px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              marginBottom: '16px',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(61, 171, 165, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Price Your Art — Free
          </button>

          {/* Micro-copy */}
          <p
            style={{
              ...ctaStyle,
              fontFamily: BRAND.fontSans,
              fontSize: '12px',
              color: BRAND.inkLight,
              marginBottom: '32px',
            }}
          >
            No signup required · 60 seconds to your first price
          </p>

          {/* Trust pills */}
          <div
            style={{
              ...pillsStyle,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            {['2,400+ artists', 'Free forever', 'USD pricing'].map((text) => (
              <div
                key={text}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: BRAND.white,
                  borderRadius: '8px',
                  fontFamily: BRAND.fontSans,
                  fontSize: '13px',
                  fontWeight: 500,
                  color: BRAND.ink,
                }}
              >
                <CheckIcon />
                {text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LandingHero;
